// Projects list (/projects) custom field columns: the pieces that are specific to this list. The column
// model, saved choices and picker section are shared with the Opportunities list - see
// shared/components/custom-field-columns.
import type React from 'react';
import moment from 'moment';
import {
	CUSTOM_FIELD_COLUMN_WIDTH_PX,
	customFieldColumnId,
	customFieldValuesFrom,
	isCustomFieldColumnId,
	type CustomFieldColumnDef,
	type CustomFieldValueConnection,
} from '../../../shared/components/custom-field-columns/customFieldColumnModel';
import {
	hideAllCustomFields,
	showAllCustomFields,
	toggleCustomField,
	visibleCustomFields,
} from '../../../shared/components/custom-field-columns/customFieldColumnPrefs';

// Saved under custom-field-columns-projects-overview-v1, apart from the Opportunities list's choices.
export const PROJECTS_CUSTOM_FIELD_SCOPE = 'projects-overview';

export interface ProjectsSortBy {
	column: string | null;
	ascending: boolean;
}

// The list's default: no sort, so rows stay in server order.
export const UNSORTED: ProjectsSortBy = {column: null, ascending: true};

// The list's table gives every cell after the first a 7px left padding outside its width.
const CELL_PADDING_LEFT_PX = 7;
// The last body cell of each row has a 16px right padding (all_projects.scss). With custom columns showing,
// it sits on the last custom column instead of the last native one.
const LAST_CELL_PADDING_RIGHT_PX = 16;

// The width the custom field columns add to the table: each fixed column plus its cell padding, and the
// last cell's right padding, which the last native column no longer carries.
export function customFieldColumnsTableWidthPx(count: number): number {
	return count ? count * (CUSTOM_FIELD_COLUMN_WIDTH_PX + CELL_PADDING_LEFT_PX) + LAST_CELL_PADDING_RIGHT_PX : 0;
}

// The table's style while custom field columns show. The table otherwise fills its container, so fixed
// columns would squeeze the native fluid columns (Progress, Remaining Work); widening it by exactly the
// custom columns' width leaves the native columns as they were and scrolls instead. Content-box keeps the
// right-hand scroll gutter the list adds to a table wider than the window (a padding) outside that width;
// under the app's border-box it would come out of the native columns. The table's sections reset to
// border-box so the cells keep their usual sizing.
export function customFieldColumnsTableStyle(count: number): React.CSSProperties | undefined {
	return count ? {width: `calc(100% + ${customFieldColumnsTableWidthPx(count)}px)`, boxSizing: 'content-box'} : undefined;
}

export interface ProjectNodeWithCustomFields {
	projectListCustomFields?: CustomFieldValueConnection | null;
}

export function projectCustomFieldValues(node: ProjectNodeWithCustomFields | null | undefined): Record<string, string | null> {
	return customFieldValuesFrom(node?.projectListCustomFields);
}

// A sort on a custom column that is not shown (turned off, or no longer available) falls back to unsorted.
export function resolveProjectsSortBy(sortBy: ProjectsSortBy, shown: CustomFieldColumnDef[]): ProjectsSortBy {
	if (!sortBy.column || !isCustomFieldColumnId(sortBy.column)) return sortBy;
	return shown.some(def => customFieldColumnId(def) === sortBy.column) ? sortBy : UNSORTED;
}

export type CustomFieldChoiceAction = {type: 'toggle'; id: string} | {type: 'showAll'} | {type: 'hideAll'};

export interface CustomFieldChoiceUpdate {
	choices: string[];
	shown: CustomFieldColumnDef[];
	sortBy: ProjectsSortBy;
	sortReset: boolean;
}

export function applyCustomFieldChoiceAction(
	choices: string[],
	action: CustomFieldChoiceAction,
	available: CustomFieldColumnDef[],
	sortBy: ProjectsSortBy
): CustomFieldChoiceUpdate {
	let next: string[];
	if (action.type === 'toggle') {
		next = toggleCustomField(choices, action.id);
	} else if (action.type === 'showAll') {
		next = showAllCustomFields(choices, available);
	} else {
		next = hideAllCustomFields(choices, available);
	}
	const shown = visibleCustomFields(next, available);
	const resolved = resolveProjectsSortBy(sortBy, shown);
	return {choices: next, shown, sortBy: resolved, sortReset: resolved !== sortBy};
}

// The picker's Custom Fields group: one more row in TheEye beside the native groups (Tasks, Project Dates...),
// whose fly-out lists every available field with its own eye. Its eye shows or hides them all.
export const CUSTOM_FIELDS_EYE_GROUP = 'customFields';
// The row's class: its fly-out opens level with the panel's top edge, capped in height and scrolling (see
// board_card_details_selector.scss).
export const CUSTOM_FIELDS_EYE_GROUP_CLASS = 'eye-option-flyout-from-top';
// A field's option name. TheEye tags each option `eye-option-<name>`, so a field's row is eye-option-cf-<key>.
const customFieldEyeOptionName = (def: CustomFieldColumnDef): string => `cf-${def.key}`;
// TheEye sorts its rows A-Z by label, or by sortOrder when set; this sorts after any label, so the group is last.
const LAST_IN_EYE = '￿';

export interface CustomFieldEyeOption {
	name: string;
	displayName: string;
	checked: boolean;
}

export interface CustomFieldEyeGroup {
	name: string;
	translationId: string;
	className: string;
	sortOrder: string;
	checked: boolean;
	nestedOptions: CustomFieldEyeOption[];
}

// Null when no field is available, so the picker shows no Custom Fields row at all.
export function customFieldEyeGroup(available: CustomFieldColumnDef[], choices: string[]): CustomFieldEyeGroup | null {
	if (available.length === 0) return null;
	const shownIds = new Set(visibleCustomFields(choices, available).map(def => def.id));
	return {
		name: CUSTOM_FIELDS_EYE_GROUP,
		translationId: 'settings.custom_fields.title',
		className: CUSTOM_FIELDS_EYE_GROUP_CLASS,
		sortOrder: LAST_IN_EYE,
		checked: shownIds.size > 0,
		nestedOptions: available.map(def => ({
			name: customFieldEyeOptionName(def),
			displayName: def.displayName,
			checked: shownIds.has(def.id),
		})),
	};
}

// What a click in the picker means for the custom field choices, or null when it was a native option.
// `selected.checked` is the state before the click, as TheEye reports it.
export function customFieldEyeAction(
	selected: {name: string; checked?: boolean},
	parent: {name: string} | null | undefined,
	available: CustomFieldColumnDef[]
): CustomFieldChoiceAction | null {
	if (selected.name === CUSTOM_FIELDS_EYE_GROUP) return selected.checked ? {type: 'hideAll'} : {type: 'showAll'};
	if (parent?.name !== CUSTOM_FIELDS_EYE_GROUP) return null;
	const def = available.find(candidate => customFieldEyeOptionName(candidate) === selected.name);
	return def ? {type: 'toggle', id: def.id} : null;
}

// Dates render like the list's own Start and End date cells: 'DD MMM' then 'YYYY'.
export function projectCustomFieldDateParts(raw: string | null | undefined): {monthDay: string; year: string} | null {
	if (raw == null || raw === '') return null;
	const date = moment(raw, 'YYYY-MM-DD', true);
	return date.isValid() ? {monthDay: date.format('DD MMM'), year: date.format('YYYY')} : null;
}
